import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { useFormContext } from "react-hook-form";
import styled from "@emotion/styled";
import { TextField, InputAdornment } from "@mui/material";
import useTenant from "../../../../hooks/useTenant";
import useUser from "../../../../hooks/useUser";
import useMapUser from "../../../../hooks/useMapUser";

const StyledAccordion = styled(Accordion)`
  margin-bottom: ${(props) => props.theme.spacing(2)};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 12px !important;
  box-shadow: none;
  overflow: hidden;
  &:before {
    display: none;
  }
  &.Mui-expanded {
    border-color: ${(props) => props.theme.palette.primary.main};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const AccordionHeader = styled(AccordionSummary)`
  background: ${(props) => props.theme.palette.background.paper};
  &:hover {
    background: #f9f9f9;
  }
`;

const UserGrid = styled(Grid)`
  padding: ${(props) => props.theme.spacing(2)} 0;
`;

const DotSettingsFields = () => {
  const { watch, setValue } = useFormContext();
  const { autoSearchTenant } = useTenant();
  const { autoSearchUser } = useUser();
  const { franchiseUsers, setFranchiseUsers } = useMapUser();

  const [franchises, setFranchises] = useState([]);
  const [loadingFranchises, setLoadingFranchises] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const selectedFranchiseIds = watch("franchiseIds") || [];
  const selectedUserIds = watch("radUserIds") || [];

  const loadFranchises = async () => {
    setLoadingFranchises(true);
    try {
      const payload = {
        filters: [],
        pagination: { pageNumber: 1, pageSize: 1000 },
        sortFields: [],
      };
      const res = await autoSearchTenant(payload);
      if (res && res.data) {
        setFranchises(res.data);
      }
    } catch (err) {
      console.error("Failed to load franchises", err);
    }
    setLoadingFranchises(false);
  };

  useEffect(() => {
    loadFranchises();
  }, []);

  useEffect(() => {
    if (selectedFranchiseIds.length > 0) {
      selectedFranchiseIds.forEach((fId) => {
        if (!franchiseUsers[fId] && !loadingUsers[fId]) {
          handleAccordionExpand(fId, true);
        }
      });
    }
  }, [selectedFranchiseIds, franchiseUsers]);

  const handleAccordionExpand = async (fId, isExpanded) => {
    if (isExpanded && !franchiseUsers[fId]) {
      setLoadingUsers((prev) => ({ ...prev, [fId]: true }));
      try {
        const payload = {
          filters: [
            {
              fieldName: "tenantId",
              fieldValue: fId,
              op: "eq",
              dataType: "long",
              condition: "and",
              isJoin: true,
            },
          ],
          pagination: { pageNumber: 1, pageSize: 1000 },
          sortFields: [],
        };
        const res = await autoSearchUser(payload);
        // autoSearchUser returns { data: [], paging: ... }
        if (res && res.data) {
          setFranchiseUsers(fId, res.data);
        }
      } catch (err) {
        console.error("Failed to load users for franchise", fId, err);
      }
      setLoadingUsers((prev) => ({ ...prev, [fId]: false }));
    }
  };

  const handleFranchiseToggle = (fId, checked) => {
    let newFranchises = [...selectedFranchiseIds];
    if (checked) {
      if (!newFranchises.includes(fId)) newFranchises.push(fId);
    } else {
      newFranchises = newFranchises.filter((id) => String(id) !== String(fId));
      // Remove users of this franchise
      const users = franchiseUsers[fId] || [];
      const userIds = users.map((u) => String(u.value || u.id || u.radUserId));
      setValue(
        "radUserIds",
        selectedUserIds.filter((id) => !userIds.includes(String(id))),
      );
    }
    setValue("franchiseIds", newFranchises);
  };

  const handleUserToggle = (fId, userId, checked) => {
    let newUsers = [...selectedUserIds];
    if (checked) {
      if (!newUsers.some((id) => String(id) === String(userId))) newUsers.push(userId);
      // Auto-select franchise if not selected
      if (!selectedFranchiseIds.some((id) => String(id) === String(fId))) {
        setValue("franchiseIds", [...selectedFranchiseIds, fId]);
      }
    } else {
      newUsers = newUsers.filter((id) => String(id) !== String(userId));
    }
    setValue("radUserIds", newUsers);
  };

  const filteredFranchises = franchises.filter((f) => {
    const name = (f.label || f.name || f.tenantName || "").toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6" fontWeight="700">
          Franchise & User Distribution
        </Typography>
        <TextField
          size="small"
          placeholder="Filter franchises..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={18} />
              </InputAdornment>
            ),
            sx: { borderRadius: "10px", width: 250 },
          }}
        />
      </Box>

      {loadingFranchises ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 10 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {filteredFranchises.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, bgcolor: "#f9f9f9", borderRadius: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  {searchQuery ? `No franchises matching "${searchQuery}"` : "No franchises found."}
                </Typography>
              </Box>
            ) : (
              filteredFranchises.map((franchise) => {
                const fId = franchise.value || franchise.id || franchise.tenantId;
                const fName = franchise.label || franchise.name || franchise.tenantName;
                const isFranchiseSelected = selectedFranchiseIds.some(
                  (id) => String(id) === String(fId),
                );
                const users = franchiseUsers[fId] || [];
                const isLoading = loadingUsers[fId];

                const areAnyUsersSelected = users.some((u) =>
                  selectedUserIds.some(
                    (id) => String(id) === String(u.value || u.id || u.radUserId),
                  ),
                );
                const areAllUsersSelected =
                  users.length > 0 &&
                  users.every((u) =>
                    selectedUserIds.some(
                      (id) => String(id) === String(u.value || u.id || u.radUserId),
                    ),
                  );

                return (
                  <StyledAccordion
                    key={`franchise-${fId}`}
                    onChange={(e, isExpanded) => handleAccordionExpand(fId, isExpanded)}
                  >
                    <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        control={
                          <Checkbox
                            checked={isFranchiseSelected}
                            indeterminate={!areAllUsersSelected && areAnyUsersSelected}
                            onChange={(e) => handleFranchiseToggle(fId, e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="subtitle2" fontWeight="600">
                              {fName}
                            </Typography>
                            {isFranchiseSelected && (
                              <Box
                                sx={{
                                  ml: 2,
                                  px: 1,
                                  py: 0.2,
                                  bgcolor: "primary.light",
                                  color: "primary.contrastText",
                                  borderRadius: "4px",
                                  fontSize: "0.6rem",
                                  fontWeight: "bold",
                                }}
                              >
                                SELECTED
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </AccordionHeader>
                    <AccordionDetails sx={{ bgcolor: "#fff", p: 3 }}>
                      {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <UserGrid container spacing={2}>
                          {users.length === 0 ? (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                No users found for this franchise.
                              </Typography>
                            </Grid>
                          ) : (
                            users.map((u) => {
                              const uId = u.value || u.id || u.radUserId;
                              const uName = u.label || u.name || u.userName;
                              const isChecked = selectedUserIds.some(
                                (id) => String(id) === String(uId),
                              );

                              return (
                                <Grid item xs={12} sm={6} md={4} key={`user-${uId}`}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: "1px solid",
                                      borderColor: isChecked ? "primary.light" : "transparent",
                                      bgcolor: isChecked ? "primary.lightest" : "transparent",
                                      borderRadius: "8px",
                                      transition: "all 0.2s",
                                      "&:hover": { bgcolor: "#f5f5f5" },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isChecked}
                                          onChange={(e) =>
                                            handleUserToggle(fId, uId, e.target.checked)
                                          }
                                        />
                                      }
                                      label={
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: isChecked ? 600 : 400 }}
                                        >
                                          {uName}
                                        </Typography>
                                      }
                                    />
                                  </Box>
                                </Grid>
                              );
                            })
                          )}
                        </UserGrid>
                      )}
                    </AccordionDetails>
                  </StyledAccordion>
                );
              })
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DotSettingsFields;
